import { createSlice, createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit'
import apiClient from '@/api/client'
import { normalizeData } from '@/utils/jsonapi'

/**
 * Entity adapter for widgets
 * Sorts widgets by position field
 */
const widgetsAdapter = createEntityAdapter({
  selectId: (widget) => widget.id,
  sortComparer: (a, b) => (a.position || 0) - (b.position || 0)
})

/**
 * Async thunk to create a widget
 * Normalizes JSON:API response before storing in Redux
 */
export const createWidget = createAsyncThunk(
  'widgets/createWidget',
  async ({ dashboardId, widgetData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        `/dashboards/${dashboardId}/widgets/`,
        widgetData
      )
      // Normalize single resource
      return normalizeData(response.data?.data || response.data)
    } catch (error) {
      return rejectWithValue(
        error.apiError?.message || error.response?.data?.message || 'Failed to create widget'
      )
    }
  }
)

/**
 * Async thunk to update a widget
 * Normalizes JSON:API response before storing in Redux
 */
export const updateWidget = createAsyncThunk(
  'widgets/updateWidget',
  async ({ dashboardId, widgetId, widgetData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(
        `/dashboards/${dashboardId}/widgets/${widgetId}/`,
        widgetData
      )
      // Normalize single resource
      return normalizeData(response.data?.data || response.data)
    } catch (error) {
      return rejectWithValue(
        error.apiError?.message || error.response?.data?.message || 'Failed to update widget'
      )
    }
  }
)

/**
 * Async thunk to move a widget into a section, or back out onto the dashboard's
 * own grid.
 *
 * Parent and position go in one PATCH on purpose. A widget's position is
 * relative to the grid it sits in, so sending them separately would leave it,
 * however briefly, inside a section at coordinates that belong to the grid it
 * came from - visible to anyone else viewing the dashboard, and the state it
 * would be left in if the second request failed.
 *
 * @param {Object} args
 * @param {string} args.dashboardId - Dashboard the widget belongs to
 * @param {string} args.widgetId - Widget being moved
 * @param {string|null} args.parentWidgetId - Section to move into, or null to
 *   move the widget out onto the dashboard's own grid
 * @param {{x: number, y: number}} [args.position] - Slot in the destination grid
 */
export const reparentWidget = createAsyncThunk(
  'widgets/reparentWidget',
  async ({ dashboardId, widgetId, parentWidgetId, position }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(
        `/dashboards/${dashboardId}/widgets/${widgetId}/`,
        {
          data: {
            type: 'widget',
            id: String(widgetId),
            attributes: {
              position_x: position?.x ?? 0,
              position_y: position?.y ?? 0,
            },
            relationships: {
              parent_widget: {
                data: parentWidgetId
                  ? { type: 'widget', id: String(parentWidgetId) }
                  : null,
              },
            },
          },
        }
      )
      return normalizeData(response.data?.data || response.data)
    } catch (error) {
      return rejectWithValue(
        error.apiError?.message || error.response?.data?.message || 'Failed to move widget'
      )
    }
  }
)

/**
 * Async thunk to delete a widget
 */
export const deleteWidget = createAsyncThunk(
  'widgets/deleteWidget',
  async ({ dashboardId, widgetId }, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/dashboards/${dashboardId}/widgets/${widgetId}/`)
      return widgetId
    } catch (error) {
      return rejectWithValue(
        error.apiError?.message || error.response?.data?.message || 'Failed to delete widget'
      )
    }
  }
)

/**
 * Widget Slice
 */
const widgetsSlice = createSlice({
  name: 'widgets',
  initialState: widgetsAdapter.getInitialState({
    loading: false,
    error: null,
  }),
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    upsertMany: (state, action) => {
      widgetsAdapter.upsertMany(state, action.payload)
    },
  },
  extraReducers: (builder) => {
    // Create Widget
    builder
      .addCase(createWidget.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createWidget.fulfilled, (state, action) => {
        state.loading = false
        widgetsAdapter.addOne(state, action.payload)
      })
      .addCase(createWidget.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // Update Widget
    builder
      .addCase(updateWidget.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateWidget.fulfilled, (state, action) => {
        state.loading = false
        widgetsAdapter.upsertOne(state, action.payload)
      })
      .addCase(updateWidget.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // Reparent Widget (into or out of a section)
    builder
      .addCase(reparentWidget.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(reparentWidget.fulfilled, (state, action) => {
        state.loading = false
        widgetsAdapter.upsertOne(state, action.payload)
      })
      .addCase(reparentWidget.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // Delete Widget
    builder
      .addCase(deleteWidget.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteWidget.fulfilled, (state, action) => {
        state.loading = false
        widgetsAdapter.removeOne(state, action.payload)
      })
      .addCase(deleteWidget.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { clearError, upsertMany } = widgetsSlice.actions

// Export adapter selectors
export const {
  selectAll: selectAllWidgets,
  selectById: selectWidgetById,
  selectIds: selectWidgetIds
} = widgetsAdapter.getSelectors((state) => state.widgets || widgetsAdapter.getInitialState())

export default widgetsSlice.reducer
